<div class="layer-row">
    <div class="layer-row-top">
        <span class="layer-index">Layer {{ index + 1 }}</span>
        <button mat-icon-button color="warn" matTooltip="Remove layer" (click)="remove.emit()">
            <mat-icon>delete</mat-icon>
        </button>
    </div>

    <div class="layer-row-grid">
        <mat-form-field appearance="fill" class="layer-key-field">
            <mat-label>Layer Key</mat-label>
            <input matInput [ngModel]="layer.layerKey" (ngModelChange)="emitField('layerKey', $event)"
                placeholder="e.g. background" />
        </mat-form-field>

        <mat-form-field appearance="fill" class="layer-type-field">
            <mat-label>Type</mat-label>
            <mat-select [ngModel]="layer.layerType" (ngModelChange)="emitField('layerType', $event)">
                @for (t of layerTypes; track t) {
                <mat-option [value]="t">{{ t }}</mat-option>
                }
            </mat-select>
        </mat-form-field>

        <mat-form-field appearance="fill" class="layer-zindex-field">
            <mat-label>Z-Index</mat-label>
            <input matInput type="number" [ngModel]="layer.zIndex" (ngModelChange)="emitField('zIndex', +$event)" />
        </mat-form-field>

        <mat-checkbox class="layer-expandable" [checked]="!!layer.isExpandable"
            (change)="emitField('isExpandable', $event.checked)">
            Expandable
        </mat-checkbox>
    </div>

    <div class="layer-section-label">Position</div>
    <div class="layer-pos-grid">
        @for (field of positionFields; track field) {
        <mat-form-field appearance="fill" class="pos-field">
            <mat-label>{{ field }}</mat-label>
            <input matInput [ngModel]="layer.position?.[field]" (ngModelChange)="emitPosition(field, $event)"
                placeholder="e.g. 0px" />
        </mat-form-field>
        }
    </div>

    @if (isBackgroundImage()) {
    <div class="layer-section-label">Background Image</div>
    <div class="slot-image-panel">
        <div class="slot-image-copy">
            <span class="slot-image-title">Layer Image</span>
            @if (pendingImageFile) {
            <p class="slot-image-hint pending">
                <mat-icon>check_circle</mat-icon>{{ pendingImageFile.name }}
            </p>
            } @else if (layer.imageUrl) {
            <div class="slot-image-preview-card">
                <img class="slot-image-preview" [src]="layer.imageUrl" alt="layer image preview" />
                <a class="slot-image-link" [href]="layer.imageUrl" target="_blank" rel="noreferrer">Open image</a>
            </div>
            } @else {
            <p class="slot-image-hint">Select an image. It will be uploaded when you save the layout.</p>
            }
        </div>
        <input #layerFileInput class="slot-file-input" type="file" accept="image/*" (change)="onFileChange($event)" />
        <button mat-stroked-button color="primary" (click)="layerFileInput.click()">
            <mat-icon>upload</mat-icon>
            {{ layer.imageUrl ? (pendingImageFile ? 'Replace Image' : 'Change Image') : 'Select Image' }}
        </button>
    </div>
    }

    @if (isTextOverlay()) {
    <div class="layer-section-label">Text Overlay</div>
    <mat-form-field appearance="fill" class="full-width">
        <mat-label>Text Template</mat-label>
        <input matInput [ngModel]="layer.textTemplate" (ngModelChange)="emitField('textTemplate', $event)"
            placeholder="e.g. Available: {count}" />
    </mat-form-field>
    <div class="layer-style-grid">
        <mat-form-field appearance="fill">
            <mat-label>Font Size</mat-label>
            <input matInput [ngModel]="layer.style?.fontSize" (ngModelChange)="emitStyle('fontSize', $event)"
                placeholder="24px" />
        </mat-form-field>
        <mat-form-field appearance="fill">
            <mat-label>Color</mat-label>
            <input matInput [ngModel]="layer.style?.color" (ngModelChange)="emitStyle('color', $event)"
                placeholder="#FFFFFF" />
        </mat-form-field>
        <mat-form-field appearance="fill">
            <mat-label>Font Weight</mat-label>
            <input matInput [ngModel]="layer.style?.fontWeight" (ngModelChange)="emitStyle('fontWeight', $event)"
                placeholder="bold" />
        </mat-form-field>
        <mat-form-field appearance="fill">
            <mat-label>Text Align</mat-label>
            <input matInput [ngModel]="layer.style?.textAlign" (ngModelChange)="emitStyle('textAlign', $event)"
                placeholder="center" />
        </mat-form-field>
        <mat-form-field appearance="fill">
            <mat-label>Background Color</mat-label>
            <input matInput [ngModel]="layer.style?.backgroundColor"
                (ngModelChange)="emitStyle('backgroundColor', $event)" placeholder="#FFD100" />
        </mat-form-field>
    </div>
    }

    @if (isComponentWidget()) {
    <div class="layer-section-label">Component Widget</div>

    <mat-form-field appearance="fill" class="full-width">
        <mat-label>Widget</mat-label>
        <mat-select [ngModel]="widgetSelection" (ngModelChange)="onWidgetSelected($event)">
            @for (w of widgets; track w.componentType) {
            <mat-option [value]="w.componentType">{{ w.label }}</mat-option>
            }
            <mat-option [value]="CUSTOM">Custom…</mat-option>
        </mat-select>
        <mat-hint>Only the widgets listed here are drawn by the display.</mat-hint>
    </mat-form-field>

    @if (selectedWidget; as widget) {
    <div class="widget-info">
        <mat-icon>info</mat-icon>
        <div class="widget-info-copy">
            <p class="widget-info-desc">{{ widget.description }}</p>
            <p class="widget-info-meta">
                Reads <code>{{ widget.liveDataKey }}</code> · available on <strong>{{ widget.states.join(', ')
                    }}</strong>
            </p>
        </div>
    </div>
    }

    @if (isCustomWidget) {
    <mat-form-field appearance="fill" class="full-width">
        <mat-label>Component Type</mat-label>
        <input matInput [ngModel]="layer.componentType" (ngModelChange)="emitField('componentType', $event)"
            placeholder="Exact widget name" />
        <mat-hint>Stored as typed. The display renders an empty box for any name it does not
            recognise.</mat-hint>
    </mat-form-field>
    }

    @if (stateMismatch; as mismatch) {
    <div class="widget-warning widget-warning--error">
        <mat-icon>report_problem</mat-icon>
        <span>{{ mismatch }}</span>
    </div>
    }

    @if (missingSize) {
    <div class="widget-warning">
        <mat-icon>aspect_ratio</mat-icon>
        <span>No width or height in <code>px</code> under Position — the display draws the code at its
            default resolution and scales it to fit the layer. A percentage does not size it.</span>
    </div>
    }

    <div class="layer-section-label">
        Component Position
        <span class="layer-section-note">not read by the display renderer — size and place the widget with
            Position above</span>
    </div>
    <div class="layer-pos-grid">
        @for (field of positionFields; track field) {
        <mat-form-field appearance="fill" class="pos-field">
            <mat-label>{{ field }}</mat-label>
            <input matInput [ngModel]="layer.componentPosition?.[field]"
                (ngModelChange)="emitComponentPosition(field, $event)" placeholder="e.g. 0px" />
        </mat-form-field>
        }
    </div>
    }
</div>